import * as THREE from 'three';

// Snake color palette from spec
export const SNAKE_COLORS = [
  '#00FFFF', '#FF00FF', '#00FF00', '#FF6600',
  '#FF0080', '#0066FF', '#FFFF00', '#9900FF',
  '#00FF80', '#FF0066', '#00CCFF', '#FF3300',
  '#66FF00', '#CC00FF', '#FFCC00'
];

export interface TurningPoint {
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
  timestamp: number;
}

export interface SnakeState {
  id: string;
  name: string;
  color: string;
  position: THREE.Vector3;
  quaternion: THREE.Quaternion;
  velocity: number;
  segments: THREE.Vector3[];
  turningPoints: TurningPoint[];
  score: number;
  reviveUsed: boolean;
  invincible: boolean;
  invincibilityTimer: number;
  isBoosting: boolean;
  isAlive: boolean;
  targetFoodId?: string; // AI target lock - pursue until eaten
  wanderUntil?: number; // Timestamp when wandering ends, undefined = not wandering
}

export interface FoodOrb {
  id: string;
  position: THREE.Vector3;
  value: number;
  color: string;
  radius: number;
}

export interface GameState {
  playerSnake: SnakeState | null;
  aiSnakes: SnakeState[];
  food: FoodOrb[];
  comets: CometState[];
  cometCatcher: string | null; // Name of snake that caught the last comet
  isPlaying: boolean;
  isPaused: boolean;
  isMuted: boolean;
}

export interface LeaderboardEntry {
  rank: number;
  name: string;
  score: number;
}

// Comet state for high-value collectibles
export interface CometState {
  id: string;
  position: THREE.Vector3;
  direction: THREE.Vector3;
  velocity: number;
  createdAt: number;
}

// Quality level type
export type QualityLevel = 'high' | 'low';

// Quality-specific settings
export const QUALITY_SETTINGS = {
  high: {
    foodCount: 1000,
    aiCount: 20,
    fogFar: 1500,
    dpr: [1, 2] as [number, number],
    antialias: true,
    bloomEnabled: true,
  },
  low: {
    foodCount: 500,
    aiCount: 10,
    fogFar: 800,
    dpr: [1, 1] as [number, number],
    antialias: false,
    bloomEnabled: false,
  },
};

// Game constants from spec
export const GAME_CONSTANTS = {
  WORLD_SIZE: 2000,
  BASE_SPEED: 50,
  BOOST_SPEED: 75,
  BOOST_DRAIN_RATE: 1, // segments per second
  INITIAL_SEGMENTS: 5,
  SEGMENT_SPACING: 3,
  SNAKE_BASE_RADIUS: 2.25, // 50% larger (was 1.5)
  FOOD_SPAWN_COUNT: 1000,
  FOOD_BASE_VALUE: 1,
  CAMERA_LERP: 0.1,
  TICK_RATE: 60,
  INVINCIBILITY_DURATION: 3000, // ms
  WAVE_AMPLITUDE: 0.3, // for swimming animation
  WAVE_FREQUENCY: 0.5,
  AI_SNAKE_COUNT: 20,
  // Comet constants
  COMET_RADIUS: 20, // Reduced by 33% (was 30)
  COMET_VELOCITY: 40, // units per second
  COMET_VALUE: 10, // 5x normal food (normal = 2 mass)
  COMET_SPAWN_INTERVAL_MIN: 60000, // 1 minute minimum
  COMET_SPAWN_INTERVAL_MAX: 180000, // 3 minutes maximum
  COMET_MAX_COUNT: 2, // max comets in world
  COMET_TRAIL_LENGTH: 50,
  COMET_COLOR_CYCLE_SPEED: 2500, // ms for full rainbow cycle
};
